import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import * as forge from 'node-forge';
import { HttpClient } from '@angular/common/http';
import { environment as ENV } from '@environments/environment';
import { apiUrls } from '@app/shared/constant/api-url';

@Injectable({
  providedIn: 'root'
})
export class EncryptionService {
  private readonly _$publicKey: BehaviorSubject<string | null> = new BehaviorSubject(null);
  private readonly _$aesKey: BehaviorSubject<string | null> = new BehaviorSubject(null);
  private _countService: number = 0;

  constructor(
    private readonly _http: HttpClient) {}

  generateRandomKey(): string {
    const actualKey = this._$aesKey.getValue();
    const cry = !actualKey ? CryptoJS.lib.WordArray.random(32).toString(CryptoJS.enc.Hex) : actualKey;
    this._$aesKey.next(cry);
    return cry;
  }

  encryptAES(data: string, sessionKey: string): string {
    const key = CryptoJS.enc.Utf8.parse(sessionKey);
    const hashedKey = CryptoJS.SHA256(key);
    const iv = CryptoJS.lib.WordArray.random(16);
    const encrypted = CryptoJS.AES.encrypt(data, hashedKey, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    const encryptedBytes = CryptoJS.enc.Base64.stringify(iv.concat(encrypted.ciphertext));
    return encryptedBytes;
  }

  decryptAES(encryptedData: string, sessionKey: string): string {
    try {
      const encryptedBytes = CryptoJS.enc.Base64.parse(encryptedData);
      const iv = CryptoJS.lib.WordArray.create(encryptedBytes.words.slice(0, 4));
      const ciphertext: any = CryptoJS.lib.WordArray.create(encryptedBytes.words.slice(4));  
      const key = CryptoJS.enc.Utf8.parse(sessionKey);
      const hashedKey = CryptoJS.SHA256(key);
      //@ts-ignore
      const decrypted = CryptoJS.AES.decrypt({ciphertext}, hashedKey, {
          iv: iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7
      });
      const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
      return decryptedString;
    } catch (err: any) {
      ENV.showLogs && console.error("Error during decryption:", err);
      return "";
    }
  }

  decryptAES2(encryptedData: string, sessionKey: string): string {
    try {
      const encryptedBytes = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
      const iv = CryptoJS.lib.WordArray.create(encryptedBytes.slice(0, 4));
      const ciphertext: any = CryptoJS.lib.WordArray.create(encryptedBytes.slice(4));  
      const key = CryptoJS.enc.Utf8.parse(sessionKey);
      const hashedKey = CryptoJS.SHA256(key);
      //@ts-ignore
      const decrypted = CryptoJS.AES.decrypt({ciphertext}, hashedKey, {
          iv: iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7
      });
      const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
      return decryptedString;
    } catch (err: any) {
      ENV.showLogs && console.error("Error during decryption:", err);
      return "";
    }
  }
 
  encryptRSA(toEncrypt: string): string {
    try {
      const publicKey: forge.pki.rsa.PublicKey = forge.pki.publicKeyFromPem(this._$publicKey.getValue());
      const encrypted = publicKey.encrypt(forge.util.encodeUtf8(toEncrypt), 'RSA-OAEP');
      return forge.util.encode64(encrypted);
    } catch (err) {
      ENV.showLogs &&  console.error("Error during decryption:", err);
      return '';
    }
  }

  encryptHybridBody(data: any, sessionKey: string) {
    ENV.showLogs && console.log("✅ New request -> ", this._countService);
    ENV.showLogs && console.log("ORIGINAL Request", JSON.stringify(data));
    return this.encryptAES(JSON.stringify(data), sessionKey);
  }

  decryptHybridBody(data: string, url?: string): any {
    try {
      const sanitizeData = JSON.parse(this.decryptAES(data, this._$aesKey.getValue() ));
      ENV.showLogs && console.log("DECRYPT RESPONSE", sanitizeData, "url", url);
      this._countService++;
      return sanitizeData;
    } catch (error) {
      ENV.showLogs && console.error("ERR DECRYPT BODY", error)      
    }
  }

  getKeyFromGateway(): Observable<boolean> {
    if( this._$publicKey.getValue() ) return of(true);
    return this._http.get(apiUrls.publicRSAKey).pipe(
      map((res: any) => { 
        if(typeof res?.message == "string"){
          const p_key: string = this._decryptBase64(res?.message);
          this._$publicKey.next(p_key);
          return true;
        }
        return false;
      }),catchError( () => of(false))
    );
  }
  
  private _decryptBase64(code64: string): string {
    const res = CryptoJS.enc.Base64.parse(code64);
    const parsedStr = res.toString(CryptoJS.enc.Utf8);
    return parsedStr;
  }

  exposePublicKey(){
    return this._$publicKey.getValue();
  }

  exposeAesKey(){
    return this._$aesKey.getValue();
  }

}
